import React, { useState } from "react";
import { Sparkles, Leaf, AlertCircle, ChevronDown, TrendingUp, Info, Utensils, Pill, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import MICRO_NOTES from "@/lib/microNotes";

// Bar colour by %RDA — green ≥85%, yellow 70-85%, orange 40-70%, red <40%
function pctColor(pct) {
  if (pct >= 85) return "#34C759";
  if (pct >= 70) return "#A3E635";
  if (pct >= 40) return "#FBBF24";
  return "#FB7185";
}

function MicroBar({ row, testId }) {
  const hitTarget = row.pct >= 100;
  const barPct = hitTarget ? 100 : Math.max(2, row.pct);
  const color = pctColor(row.pct);
  return (
    <div className="py-2" data-testid={testId}>
      <div className="flex items-baseline justify-between mb-1 gap-2">
        <span className="text-xs uppercase tracking-wider text-zinc-200 truncate">{row.label}</span>
        <span className="text-[11px] font-mono shrink-0 flex items-center gap-1.5">
          <span className="text-white">{row.amount}{row.unit}</span>
          <span className="text-zinc-400">/ {row.target}{row.unit}</span>
          {hitTarget ? (
            <span className="inline-flex items-center gap-1 text-[#34C759] font-sans font-semibold uppercase text-[10px] tracking-wider" data-testid={`hit-${row.key}`}>
              <Check className="h-3 w-3" /> Target reached
            </span>
          ) : (
            <span style={{ color }}>{row.pct}%</span>
          )}
        </span>
      </div>
      <div className="h-1.5 bg-white/5 relative overflow-hidden">
        <div className="absolute inset-y-0 left-0 transition-all" style={{ width: `${barPct}%`, backgroundColor: color }} />
      </div>
      {/* Plain-English "what it does" so athletes new to micronutrients
          instantly understand why each row matters. Same copy shared with
          the macro-only Vitamins Reference via @/lib/microNotes. */}
      {MICRO_NOTES[row.key] && <p className="text-[10px] text-zinc-400 mt-1 leading-snug">{MICRO_NOTES[row.key]}</p>}
    </div>
  );
}

export default function WeeklyMicros({ micros }) {
  const [showFull, setShowFull] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  if (!micros || !micros.weekly) return null;
  const { weekly, veggie6, nudges, keys } = micros;
  const headline = veggie6.map((k) => ({ key: k, ...weekly[k] })).filter((r) => r);
  const rest = keys.filter((k) => !veggie6.includes(k)).map((k) => ({ key: k, ...weekly[k] })).filter((r) => r);

  return (
    <div className="border border-white/10 bg-[#141414]" data-testid="weekly-micros">
      <header className="flex items-center gap-2 px-4 py-3 border-b border-white/5">
        <Leaf className="h-4 w-4 text-[#A3E635]" />
        <h3 className="font-display uppercase tracking-[0.15em] text-sm text-white font-bold">Weekly Micros</h3>
        <button
          type="button"
          data-testid="micros-info-btn"
          onClick={() => setShowInfo(true)}
          className="p-1 -m-1 text-zinc-300 hover:text-[#E2E30A] transition-colors"
          aria-label="How the micros are estimated"
          title="How the micros are estimated"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
        <span className="text-[10px] uppercase tracking-wider text-zinc-400 ml-auto flex items-center gap-1">
          <TrendingUp className="h-3 w-3" /> vs NHS weekly RDA
        </span>
      </header>

      <div className="p-4">
        <p className="text-[11px] text-zinc-300 mb-3 leading-relaxed">
          <span className="text-white">From your selected meals this week.</span> The six shown here are the ones most likely to run low under endurance training — iron & B12 for oxygen carrying, calcium & vitamin D for bone stress load, zinc for immune recovery, omega-3 for inflammation control. Expand the full panel for the other 14.
        </p>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1" data-testid="micros-veggie6">
          {headline.map((row) => <MicroBar key={row.key} row={row} testId={`micro-${row.key}`} />)}
        </div>

        {nudges.length > 0 && (
          <div className="mt-5" data-testid="micros-nudges">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="h-3.5 w-3.5 text-[#FBBF24]" />
              <h4 className="font-display uppercase tracking-[0.15em] text-xs text-[#FBBF24] font-bold">Top-ups needed</h4>
              <span className="text-[10px] uppercase tracking-wider text-zinc-400 ml-auto">{nudges.length} short</span>
            </div>
            <div className="space-y-2">
              {nudges.map((n) => (
                <div key={n.key} className="border border-[#FBBF24]/25 bg-[#FBBF24]/[0.04] px-3 py-2.5" data-testid={`nudge-${n.key}`}>
                  <div className="flex items-baseline justify-between gap-2 mb-1.5">
                    <span className="text-xs uppercase tracking-wider text-[#FBBF24] font-semibold">{n.label}</span>
                    <span className="text-[11px] font-mono">
                      <span className="text-white">{n.pct}%</span>
                      <span className="text-zinc-400"> · gap </span>
                      <span className="text-[#FBBF24]">{n.gap}{n.unit}</span>
                      <span className="text-zinc-400"> ({n.gapPerDay}{n.unit}/day)</span>
                    </span>
                  </div>
                  <p className="text-[11px] text-zinc-100 leading-snug flex items-start gap-1.5">
                    <Utensils className="h-3 w-3 text-[#A3E635] mt-[3px] shrink-0" />
                    <span><b className="text-[#A3E635]">Food first:</b> {n.food}</span>
                  </p>
                  {n.supplement && (
                    <p className="text-[11px] text-zinc-300 leading-snug flex items-start gap-1.5 mt-1">
                      <Pill className="h-3 w-3 text-[#4C9AFF] mt-[3px] shrink-0" />
                      <span><b className="text-[#4C9AFF]">Supplement:</b> {n.supplement}</span>
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <button type="button" data-testid="micros-full-toggle" onClick={() => setShowFull((v) => !v)}
          className="mt-4 text-[10px] uppercase tracking-wider text-[#E2E30A] hover:text-white flex items-center gap-1 transition-colors">
          <Sparkles className="h-3 w-3" /> {showFull ? "Hide full panel" : "Show full micronutrient panel"}
          <ChevronDown className={`h-3 w-3 transition-transform ${showFull ? "rotate-180" : ""}`} />
        </button>

        {showFull && (
          <div className="mt-3 pt-3 border-t border-white/5 grid sm:grid-cols-2 gap-x-6 gap-y-1" data-testid="micros-full-panel">
            {rest.map((row) => <MicroBar key={row.key} row={row} testId={`micro-${row.key}`} />)}
          </div>
        )}

        <p className="mt-4 text-[10px] text-zinc-400 leading-relaxed">
          Estimates only — based on the aggregate ingredient list, not lab analysis. Treat as a guide, not a prescription. Individual needs vary; consult a Registered Sports Dietitian before starting any supplement.
        </p>
      </div>

      <Dialog open={showInfo} onOpenChange={setShowInfo}>
        <DialogContent data-testid="micros-info-dialog" className="max-w-md rounded-none bg-[#141414] border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-[0.15em] text-lg text-[#E2E30A]">About these numbers</DialogTitle>
            <DialogDescription className="text-zinc-300 text-sm">A quick read on what you're seeing.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm text-zinc-100 leading-relaxed">
            <p><b className="text-white">What it is:</b> an estimate of the vitamins & minerals your planned meals deliver this week, compared to the NHS daily RDA × 7.</p>
            <p><b className="text-white">How to read it:</b> a <span className="text-[#34C759] font-semibold">green tick</span> means you've hit the target from food. A <span className="text-[#FBBF24]">%</span> under 100 shows how far off you are — with a food-first top-up (and a supplement fallback) below.</p>
            <p><b className="text-white">Fair warning:</b> the RDA is a population floor, not an athlete target — big numbers on Omega-3 / Vit C / B-vits from real meals are expected. Estimates only; treat as a guide, not a prescription.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
